import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import ShareSlipModal from '../components/ShareSlipModal';
import './SalaryDetail.css';

const thaiMonths = [
    '', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

function formatNumber(num) {
    if (num === null || num === undefined) return '0.00';
    return Number(num).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function SalaryDetail() {
    const { yyyy, mm } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [slipRequestMode, setSlipRequestMode] = useState(false); // default ปิด (พิมพ์ได้เลย)
    const [printing, setPrinting] = useState(false);

    // Print/email modal
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [emailing, setEmailing] = useState(false);
    const [emailResult, setEmailResult] = useState('');

    // แชร์สลิปเป็นรูปภาพ
    const [showShareModal, setShowShareModal] = useState(false);

    const emailVerified = !!user?.email_verified;

    useEffect(() => {
        fetchData();
    }, [yyyy, mm]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [detailRes, settingsRes] = await Promise.all([
                api.get(`/salary/detail/${yyyy}/${mm}`),
                api.get('/request-mode').catch(() => ({ data: { slip_request_mode: false } }))
            ]);
            setData(detailRes.data);
            setSlipRequestMode(settingsRes.data.slip_request_mode ?? false);
        } catch (err) {
            setError(err.response?.data?.error || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
        } finally {
            setLoading(false);
        }
    };

    // เปิด modal แจ้งเตือนก่อนพิมพ์ (กันปัญหา popup ถูกบล็อกบนมือถือ)
    const handlePrint = () => {
        if (!emailVerified) { window.location.href = '/profile#email'; return; }
        setEmailResult('');
        setShowPrintModal(true);
    };

    // เปิด/พิมพ์ PDF (พฤติกรรมเดิม)
    const openPdf = async () => {
        if (!emailVerified) { window.location.href = '/profile#email'; return; }
        try {
            setPrinting(true);
            const response = await api.get(`/report/slip/${yyyy}/${mm}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            if (!win) {
                // popup ถูกบล็อก — ดาวน์โหลดแทน
                const a = document.createElement('a');
                a.href = url;
                a.download = `salary_slip_${parseInt(yyyy) + 543}_${mm}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
            setShowPrintModal(false);
        } catch (err) {
            console.error('Print error:', err);
            alert('ไม่สามารถพิมพ์เอกสารได้');
        } finally {
            setPrinting(false);
        }
    };

    // ส่งสลิปเข้าอีเมลที่ยืนยันแล้ว
    const sendToEmail = async () => {
        try {
            setEmailing(true);
            setEmailResult('');
            const res = await api.post(`/report/slip/${yyyy}/${mm}/email`);
            setEmailResult(res.data.message || 'ส่งเข้าอีเมลแล้ว');
        } catch (err) {
            setEmailResult(err.response?.data?.message || err.response?.data?.error || 'ส่งอีเมลไม่สำเร็จ');
        } finally {
            setEmailing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex-center" style={{ padding: '60px 0' }}>
                <div className="spinner"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-error">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                {error}
            </div>
        );
    }

    const { header, income, deduction, summary } = data;

    // คำนวณยอดภาษี (รหัส 501) และ กบข. (รหัส 601, 602)
    const taxAmount = deduction
        .filter(item => item.code === '501')
        .reduce((sum, item) => sum + Math.abs(item.amt || 0), 0);

    const gpfAmount = deduction
        .filter(item => item.code === '601' || item.code === '602')
        .reduce((sum, item) => sum + Math.abs(item.amt || 0), 0);

    // เดือนนี้ยังไม่มีข้อมูลจริง (ไม่มีรายการ + ทุกยอดเป็น 0) = เจ้าหน้าที่กำลังจัดเตรียมข้อมูล
    const isPreparingDetail =
        (income?.length || 0) === 0 &&
        (deduction?.length || 0) === 0 &&
        !(summary?.totalIncome) && !(summary?.totalDeduction) && !(summary?.netPay);

    if (isPreparingDetail) {
        return (
            <div className="salary-detail">
                <div className="breadcrumb">
                    <Link to="/">หน้าหลัก</Link>
                    <span>/</span>
                    <span>สลิปเงินเดือน {thaiMonths[parseInt(mm)]} {parseInt(yyyy) + 543}</span>
                </div>
                <div className="detail-header">
                    <div className="header-info">
                        <h1>สลิปเงินเดือน</h1>
                        <p className="month-title">{thaiMonths[parseInt(mm)]} พ.ศ. {parseInt(yyyy) + 543}</p>
                    </div>
                </div>
                <div className="steam-card">
                    <div className="steam-card-body">
                        <div className="detail-preparing">
                            <span className="prep-dots" aria-hidden="true"><i></i><i></i><i></i></span>
                            <h2>กำลังจัดเตรียมข้อมูล</h2>
                            <p>
                                เจ้าหน้าที่กำลังบันทึกข้อมูลเงินเดือนของเดือนนี้<br />
                                โดยปกติจะเสร็จสิ้นภายในสัปดาห์แรกของเดือน<br />
                                เมื่อข้อมูลพร้อม ระบบจะแสดงรายละเอียดสลิปให้อัตโนมัติ
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="salary-detail">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/">หน้าหลัก</Link>
                <span>/</span>
                <span>สลิปเงินเดือน {thaiMonths[parseInt(mm)]} {parseInt(yyyy) + 543}</span>
            </div>

            {/* Header */}
            <div className="detail-header">
                <div className="header-info">
                    <h1>สลิปเงินเดือน</h1>
                    <p className="month-title">{thaiMonths[parseInt(mm)]} พ.ศ. {parseInt(yyyy) + 543}</p>
                </div>
                <div className="header-actions">
                    {/* แชร์สลิปเป็นรูปภาพ (เลือกองค์ประกอบได้ก่อนบันทึกลงแกลเลอรี) */}
                    <button
                        className="share-slip-btn"
                        onClick={() => setShowShareModal(true)}
                        title="แชร์สลิปเป็นรูปภาพ — บันทึกลงแกลเลอรี / LINE / Facebook / Instagram / TikTok"
                    >
                        {/* ไอคอนแชร์แบบมาตรฐาน (กล่อง + ลูกศรขึ้น) ที่ผู้ใช้คุ้นจากมือถือ */}
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7"/>
                            <polyline points="16 6 12 2 8 6"/>
                            <line x1="12" y1="2" x2="12" y2="15"/>
                        </svg>
                        แชร์
                    </button>
                    {slipRequestMode ? (
                        <Link to="/request" className="steam-btn steam-btn-primary">
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 12h6M9 16h6M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2z"/>
                            </svg>
                            ขอสลิปเงินเดือน
                        </Link>
                    ) : (
                        <button
                            className="steam-btn steam-btn-primary"
                            onClick={handlePrint}
                            disabled={printing || !emailVerified}
                            title={!emailVerified ? 'ต้องยืนยันอีเมลก่อนจึงจะพิมพ์ได้' : ''}
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
                            </svg>
                            {printing ? 'กำลังพิมพ์...' : (emailVerified ? 'พิมพ์สลิปเงินเดือน' : 'ยืนยันอีเมลก่อนพิมพ์')}
                        </button>
                    )}
                </div>
            </div>

            {/* Employee Info */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">ข้อมูลพนักงาน</div>
                <div className="steam-card-body">
                    <div className="emp-info-grid">
                        <div className="emp-info-item">
                            <span className="label">ชื่อ-นามสกุล:</span>
                            <span className="value">{header.prename}{header.fullname}</span>
                        </div>
                        <div className="emp-info-item">
                            <span className="label">หน่วยต้นทุน:</span>
                            <span className="value">{header.depts}: {header.dept_desc || ''}</span>
                        </div>
                        <div className="emp-info-item">
                            <span className="label">เลขที่บัญชี:</span>
                            <span className="value">{header.pre_code}{header.emp_ac_no?.replace(header.pre_code, '')}</span>
                        </div>
                        <div className="emp-info-item">
                            <span className="label">ตำแหน่ง:</span>
                            <span className="value">{header.positionname || '-'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary */}
            <div className="summary-boxes">
                <div className="summary-box income">
                    <span className="summary-label">รวมรับ</span>
                    <span className="summary-value">{formatNumber(summary.totalIncome)}</span>
                    <span className="summary-unit">บาท</span>
                </div>
                <div className="summary-box deduction">
                    <span className="summary-label">รวมหัก</span>
                    <span className="summary-value">{formatNumber(summary.totalDeduction)}</span>
                    <span className="summary-unit">บาท</span>
                </div>
                <div className="summary-box net">
                    <span className="summary-label">รับสุทธิ</span>
                    <span className="summary-value">{formatNumber(summary.netPay)}</span>
                    <span className="summary-unit">บาท</span>
                </div>
            </div>

            {/* Tax & GPF Summary */}
            <div className="tax-gpf-summary">
                <div className="tax-gpf-box tax">
                    <span className="tax-gpf-icon">
                        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                        </svg>
                    </span>
                    <div className="tax-gpf-info">
                        <span className="tax-gpf-label">ภาษี หัก ณ ที่จ่าย</span>
                        <span className="tax-gpf-value">{formatNumber(taxAmount)} บาท</span>
                    </div>
                </div>
                {gpfAmount > 0 && (
                    <div className="tax-gpf-box gpf">
                        <span className="tax-gpf-icon">
                            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
                            </svg>
                        </span>
                        <div className="tax-gpf-info">
                            <span className="tax-gpf-label">กบข. (กองทุนบำเหน็จบำนาญ)</span>
                            <span className="tax-gpf-value">{formatNumber(gpfAmount)} บาท</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Tables */}
            <div className="detail-tables">
                {/* Income Table */}
                <div className="steam-card">
                    <div className="steam-card-header">
                        <span>รายการรับ</span>
                        <span className="badge badge-success">{income.length} รายการ</span>
                    </div>
                    <div className="steam-card-body" style={{ padding: 0 }}>
                        <table className="steam-table">
                            <thead>
                                <tr>
                                    <th>รหัส</th>
                                    <th>รายการ</th>
                                    <th className="text-right">จำนวนเงิน</th>
                                </tr>
                            </thead>
                            <tbody>
                                {income.map((item, idx) => (
                                    <tr key={idx}>
                                        <td>{item.code}</td>
                                        <td>{item.acc_name || `รหัส ${item.code}`}</td>
                                        <td className="text-right text-success">{formatNumber(item.amt)}</td>
                                    </tr>
                                ))}
                                <tr className="total-row">
                                    <td colSpan="2"><strong>รวมรายรับ</strong></td>
                                    <td className="text-right"><strong>{formatNumber(summary.totalIncome)}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Deduction Table */}
                <div className="steam-card">
                    <div className="steam-card-header">
                        <span>รายการหัก</span>
                        <span className="badge badge-danger">{deduction.length} รายการ</span>
                    </div>
                    <div className="steam-card-body" style={{ padding: 0 }}>
                        <table className="steam-table">
                            <thead>
                                <tr>
                                    <th>รหัส</th>
                                    <th>รายการ</th>
                                    <th className="text-right">จำนวนเงิน</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deduction.map((item, idx) => (
                                    <tr key={idx}>
                                        <td>{item.code}</td>
                                        <td>{item.acc_name || `รหัส ${item.code}`}</td>
                                        <td className="text-right text-danger">{formatNumber(Math.abs(item.amt))}</td>
                                    </tr>
                                ))}
                                <tr className="total-row">
                                    <td colSpan="2"><strong>รวมรายหัก</strong></td>
                                    <td className="text-right"><strong>{formatNumber(summary.totalDeduction)}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Share as image modal */}
            {showShareModal && (
                <ShareSlipModal data={data} yyyy={yyyy} mm={mm} onClose={() => setShowShareModal(false)} />
            )}

            {/* Print / Email modal */}
            {showPrintModal && (
                <div className="print-modal-overlay" onClick={() => !printing && !emailing && setShowPrintModal(false)}>
                    <div className="print-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="print-modal-close" onClick={() => setShowPrintModal(false)} disabled={printing || emailing}>&times;</button>
                        <h3>พิมพ์ / รับสลิปเงินเดือน</h3>

                        <div className="print-notice">
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                            </svg>
                            <span>
                                หากพิมพ์ผ่านมือถือ/แท็บเล็ตแล้วเอกสารไม่เปิด (มักเกิดจากเบราว์เซอร์บล็อกหน้าต่างใหม่)
                                <strong> ไม่ต้องกดซ้ำหลายครั้ง</strong> — แนะนำให้ <strong>ส่งเข้าอีเมล</strong> แล้วเปิดไฟล์จากอีเมลแทน
                            </span>
                        </div>

                        {/* ตัวเลือก 1: เปิด/พิมพ์ */}
                        <button className="print-option-btn primary" onClick={openPdf} disabled={printing || emailing}>
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/></svg>
                            {printing ? 'กำลังเปิดเอกสาร...' : 'เปิด / พิมพ์เอกสาร'}
                        </button>

                        {/* ตัวเลือก 2: ส่งอีเมล */}
                        {emailVerified ? (
                            <button className="print-option-btn secondary" onClick={sendToEmail} disabled={printing || emailing}>
                                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                                {emailing ? 'กำลังส่ง...' : 'ส่งเข้าอีเมลของฉัน'}
                            </button>
                        ) : (
                            <div className="print-email-cta">
                                <p>อยากรับเอกสารทางอีเมล? ยืนยันอีเมลของคุณก่อน แล้วระบบจะส่งไฟล์ให้อัตโนมัติ</p>
                                <Link to="/profile" className="print-option-btn secondary">
                                    ไปยืนยันอีเมล
                                </Link>
                            </div>
                        )}

                        {emailResult && (
                            <div className="print-email-result">{emailResult}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default SalaryDetail;
